import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TimezoneSelect } from '@/components/ui/timezone-select';
import {
  updateOrganisationDetails,
  getOrganisationDetails,
  getOrganisationMembers,
  inviteUserToOrganisation,
  listOrganisationInvites,
  cancelOrganisationInvite,
  extendOrganisationInvite,
  getUserProfile,
  getOrganisationBillingPlan,
} from '@/client/sdk.gen';
import type {
  UpdateOrganisationPayload,
  OrganisationMemberResponse,
  OrganisationRole,
  OrganisationInviteRead,
  BillingPlanResponse,
} from '@/client/types.gen';
import DashboardLayout from '@/app/dashboard/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Mail, RefreshCw, CreditCard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  phone: z.string().optional(),
  timezone: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

// Add new invite form schema
const inviteFormSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  role: z.enum(['ADMIN', 'MEMBER'] as const).default('MEMBER'),
});

type InviteFormValues = z.infer<typeof inviteFormSchema>;

export default function TeamDetails() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [members, setMembers] = useState<OrganisationMemberResponse[]>([]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invites, setInvites] = useState<OrganisationInviteRead[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [cancelInviteLoading, setCancelInviteLoading] = useState<string | null>(
    null
  );
  const [resendInviteLoading, setResendInviteLoading] = useState<string | null>(
    null
  );
  const [billingPlan, setBillingPlan] = useState<BillingPlanResponse | null>(
    null
  );
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [hasReachedLimit, setHasReachedLimit] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      phone: '',
      timezone: '',
    },
  });

  const inviteForm = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      email: '',
      role: 'MEMBER',
    },
  });

  useEffect(() => {
    const loadOrganisationDetails = async () => {
      try {
        const response = await getOrganisationDetails();
        if (response.data) {
          form.reset({
            name: response.data.name,
            description: response.data.description || '',
            phone: response.data.phone || '',
            timezone: response.data.timezone || '',
          });
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to load team details. Please refresh the page.',
          variant: 'destructive',
        });
      } finally {
        setInitialLoading(false);
      }
    };

    loadOrganisationDetails();
  }, [form, toast]);

  const loadMembers = useCallback(async () => {
    try {
      const response = await getOrganisationMembers();
      if (response.data) {
        setMembers(response.data.members);
      }
    } catch (error) {
      console.error('Error loading members:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team members. Please refresh the page.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const loadInvites = useCallback(async () => {
    setInvitesLoading(true);
    try {
      const response = await listOrganisationInvites();
      if (response.data) {
        setInvites(response.data.invites);
      }
    } catch (error) {
      console.error('Error loading invites:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team invites. Please refresh the page.',
        variant: 'destructive',
      });
    } finally {
      setInvitesLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    const checkIfAdmin = async () => {
      try {
        const response = await getUserProfile();
        if (response.data) {
          // Check user role by getting organization members
          const membersResponse = await getOrganisationMembers();
          if (membersResponse.data) {
            const currentUserEmail = response.data.email;
            const userMember = membersResponse.data.members.find(
              (member) => member.email === currentUserEmail
            );
            if (userMember && userMember.role === 'ADMIN') {
              setIsAdmin(true);
              // Load invites only if user is admin
              loadInvites();
            }
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    checkIfAdmin();
  }, [loadInvites]);

  useEffect(() => {
    const loadBillingPlan = async () => {
      try {
        const response = await getOrganisationBillingPlan();
        if (response.data) {
          setBillingPlan(response.data.billing_plan);
        }
      } catch (error) {
        console.error('Error loading billing plan:', error);
      }
    };

    loadBillingPlan();
  }, []);

  // Check if we've reached or exceeded the plan limit
  useEffect(() => {
    if (billingPlan && members && invites) {
      const totalMembersAndInvites = members.length + invites.length;
      if (totalMembersAndInvites >= billingPlan.max_users) {
        setHasReachedLimit(true);
      } else {
        setHasReachedLimit(false);
      }
    }
  }, [billingPlan, members, invites]);

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      const payload: UpdateOrganisationPayload = {
        name: values.name,
        description: values.description || null,
        phone: values.phone || null,
        timezone: values.timezone || null,
      };

      const response = await updateOrganisationDetails({
        body: payload,
      });

      if (response.data) {
        toast({
          title: 'Success',
          description: 'Team details updated successfully.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update team details. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const onInviteMember = async (values: InviteFormValues) => {
    // Check if we're at the limit before sending
    if (hasReachedLimit) {
      setShowUpgradeDialog(true);
      return;
    }

    setInviteLoading(true);
    try {
      await inviteUserToOrganisation({
        body: {
          email: values.email,
          role: values.role,
        },
      });

      toast({
        title: 'Success',
        description: 'Invitation sent successfully.',
      });
      setIsInviteDialogOpen(false);
      inviteForm.reset();
      loadMembers(); // Refresh member list
      loadInvites(); // Refresh invites list
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send invitation. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    setCancelInviteLoading(inviteId);
    try {
      await cancelOrganisationInvite({
        path: {
          invite_id: inviteId,
        },
      });

      toast({
        title: 'Success',
        description: 'Invitation cancelled successfully.',
      });

      // Refresh invites and members
      loadInvites();
      loadMembers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to cancel invitation. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCancelInviteLoading(null);
    }
  };

  const handleResendInvite = async (inviteId: string) => {
    setResendInviteLoading(inviteId);
    try {
      await extendOrganisationInvite({
        path: {
          invite_id: inviteId,
        },
      });

      toast({
        title: 'Success',
        description: 'Invitation extended and resent successfully.',
      });

      // Refresh invites
      loadInvites();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resend invitation. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setResendInviteLoading(null);
    }
  };

  return (
    <DashboardLayout
      breadcrumbs={[
        { label: 'Home', href: '/dashboard' },
        { label: 'Team Management', href: '#' },
        { label: 'Team Details', href: '/dashboard/team-management/settings' },
      ]}
    >
      <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-120px)]">
        {/* Plan limit warning */}
        {hasReachedLimit && (
          <Alert variant="warning" className="mb-4">
            <AlertTitle>Team Member Limit Reached</AlertTitle>
            <AlertDescription>
              You have reached the maximum number of team members for your
              current plan. To invite more members, please upgrade your plan.
              <Button
                variant="outline"
                size="sm"
                className="ml-2"
                onClick={() => setShowUpgradeDialog(true)}
              >
                Upgrade Now
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Team Details</CardTitle>
          </CardHeader>
          <CardContent>
            {initialLoading ? (
              <div className="space-y-6">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-[120px]" />
              </div>
            ) : (
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Team Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter team name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter team description"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                          <Input
                            type="tel"
                            placeholder="Enter phone number"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="timezone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Timezone</FormLabel>
                        <FormControl>
                          <TimezoneSelect
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder="Select timezone"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" disabled={loading || initialLoading}>
                    {loading ? 'Updating...' : 'Update Team Details'}
                  </Button>
                </form>
              </Form>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Team Members</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <a href="/dashboard/team-management/invites">
                  <Mail className="mr-2 h-4 w-4" />
                  View Invites
                </a>
              </Button>
              <Dialog
                open={isInviteDialogOpen}
                onOpenChange={setIsInviteDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite Member
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Invite Team Member</DialogTitle>
                    <DialogDescription>
                      Send an invitation to join your team. They'll receive an
                      email with instructions.
                    </DialogDescription>
                  </DialogHeader>
                  {hasReachedLimit && (
                    <Alert variant="warning" className="mb-4">
                      <AlertTitle>Team Member Limit Reached</AlertTitle>
                      <AlertDescription>
                        You have reached the maximum number of{' '}
                        {billingPlan?.max_users || ''} team members for your
                        current plan. Please upgrade to invite more members.
                      </AlertDescription>
                    </Alert>
                  )}
                  <Form {...inviteForm}>
                    <form
                      onSubmit={inviteForm.handleSubmit(onInviteMember)}
                      className="space-y-4"
                    >
                      <FormField
                        control={inviteForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter email address"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={inviteForm.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Role</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a role" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="MEMBER">Member</SelectItem>
                                <SelectItem value="ADMIN">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={inviteLoading || hasReachedLimit}
                      >
                        {inviteLoading
                          ? 'Sending Invitation...'
                          : hasReachedLimit
                          ? 'Plan Limit Reached'
                          : 'Send Invitation'}
                      </Button>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.email}>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          member.role === 'ADMIN' ? 'default' : 'secondary'
                        }
                      >
                        {member.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {member.user_id ? (
                        <Badge variant="default">Active</Badge>
                      ) : member.invite_sent_at ? (
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">Pending</Badge>
                          <span className="text-xs text-muted-foreground">
                            {member.invite_expires_at &&
                            new Date(member.invite_expires_at) > new Date()
                              ? `Expires ${new Date(
                                  member.invite_expires_at
                                ).toLocaleDateString()}`
                              : 'Invite Expired'}
                          </span>
                        </div>
                      ) : (
                        <Badge variant="destructive">Not Invited</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {members.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-4">
                      No team members found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pending Invites Card - Only visible to admins */}
        {isAdmin && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Pending Team Invites</CardTitle>
              <Button
                variant="outline"
                onClick={loadInvites}
                disabled={invitesLoading}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {invitesLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </CardHeader>
            <CardContent>
              {invitesLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Invited By</TableHead>
                      <TableHead>Date Sent</TableHead>
                      <TableHead>Expires On</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invites.map((invite) => (
                      <TableRow key={invite.id}>
                        <TableCell>{invite.email}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              invite.role === 'ADMIN' ? 'default' : 'secondary'
                            }
                          >
                            {invite.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {invite.invited_by_id || 'System'}
                        </TableCell>
                        <TableCell>
                          {invite.created_at
                            ? new Date(invite.created_at).toLocaleDateString()
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {!invite.accepted_at ? (
                            <span className="text-green-500">
                              Active - Sent{' '}
                              {new Date(invite.created_at).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-red-500">Accepted</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResendInvite(invite.id)}
                              disabled={
                                resendInviteLoading === invite.id ||
                                invite.accepted_at !== null // Disable if already accepted
                              }
                            >
                              {resendInviteLoading === invite.id
                                ? 'Resending...'
                                : 'Resend'}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleCancelInvite(invite.id)}
                              disabled={cancelInviteLoading === invite.id}
                            >
                              {cancelInviteLoading === invite.id
                                ? 'Cancelling...'
                                : 'Cancel'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {invites.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-4">
                          No pending invites found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Upgrade Plan Dialog */}
        <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upgrade Your Plan</DialogTitle>
              <DialogDescription>
                Your current plan allows a maximum of{' '}
                {billingPlan?.max_users || 'limited'} team members. Upgrade now
                to invite more team members and access additional features.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col space-y-4 py-4">
              <div className="flex items-center justify-between border p-4 rounded-lg">
                <div>
                  <h3 className="font-medium">
                    Current Plan: {billingPlan?.name || 'Free'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {billingPlan?.max_users || 0} team members maximum
                  </p>
                </div>
                <Badge>
                  {billingPlan?.price_per_month_per_user_in_cents
                    ? `$${
                        billingPlan.price_per_month_per_user_in_cents / 100
                      }/user/month`
                    : 'Free'}
                </Badge>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowUpgradeDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => (window.location.href = '/dashboard/billing')}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                View Plans
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
